import { Component } from 'react';
import analytics, { FIELD_IDS } from '../../services/analytics';
import { verifySmsCode } from '../../services/sms-verification';

const STEP_NUMBER = 4;
const SMS_CODE_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 30;

class PhoneConfirmation extends Component {
	constructor(props) {
		super(props);
		this.fieldTracker = analytics.getFieldTracker();
		this._resendCooldownInterval = null;
		this.state = {
			smsCode: props.initialValue || '',
			errors: [],
			verifying: false,
			phoneVerified: false,
			resendCooldownSeconds: 0,
		};
	}

	componentDidMount() {
		if (this.props.smsSent) {
			this.startResendCooldown();
		}
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.smsSent && this.props.smsSent) {
			this.startResendCooldown();
		}
	}

	componentWillUnmount() {
		this.clearResendCooldown();
	}

	clearResendCooldown = () => {
		if (this._resendCooldownInterval) {
			clearInterval(this._resendCooldownInterval);
			this._resendCooldownInterval = null;
		}
	}

	startResendCooldown = () => {
		this.clearResendCooldown();
		this.setState({ resendCooldownSeconds: RESEND_COOLDOWN_SECONDS });

		this._resendCooldownInterval = setInterval(() => {
			this.setState((prevState) => {
				const next = prevState.resendCooldownSeconds - 1;
				if (next <= 0) {
					this.clearResendCooldown();
					return { resendCooldownSeconds: 0 };
				}
				return { resendCooldownSeconds: next };
			});
		}, 1000);
	}

	handleResendClick = () => {
		const { onResendSms, smsSending } = this.props;
		const { resendCooldownSeconds } = this.state;

		if (resendCooldownSeconds > 0 || smsSending || !onResendSms) {
			return;
		}

		onResendSms();
		this.startResendCooldown();
	}

	isResendDisabled = () => {
		const { smsSending, verifying } = this.props;
		return smsSending || verifying || this.state.resendCooldownSeconds > 0;
	}

	getResendLabel = (baseLabel) => {
		const { resendCooldownSeconds } = this.state;
		if (resendCooldownSeconds > 0) {
			return `${baseLabel} (${resendCooldownSeconds}s)`;
		}
		return baseLabel;
	}

	maskPhoneNumber = (phoneNumber) => {
		const clean = (phoneNumber || '').replace(/\s/g, '');
		if (clean.length !== 10) return phoneNumber || '';
		return `${clean.slice(0, 2)} ${clean.slice(2, 4)} ** ** ${clean.slice(8, 10)}`;
	}

	handleChange = (e) => {
		const smsCode = e.target.value.replace(/\D/g, '').substring(0, SMS_CODE_LENGTH);
		this.fieldTracker.onFieldChange(
			STEP_NUMBER,
			FIELD_IDS.STEP_FINAL_QUESTIONS.contact_sms_code,
			smsCode
		);
		this.setState({ smsCode, errors: [] }, () => {
			if (this.props.onPhoneConfirmationChange) {
				this.props.onPhoneConfirmationChange(this.getData());
			}
		});
	}

	getData = () => ({
		smsCode: this.state.smsCode.replace(/\s/g, ''),
		phoneVerified: this.state.phoneVerified === true,
	});

	validateSelection = () => {
		const cleanCode = this.state.smsCode.replace(/\s/g, '');
		let errors = [];

		if (!cleanCode) {
			errors.push('Veuillez saisir le code reçu par SMS.');
			analytics.trackValidationError(STEP_NUMBER, 'SMS_CODE_REQUIRED', 'smsCode');
		} else if (cleanCode.length !== SMS_CODE_LENGTH) {
			errors.push(`Veuillez saisir le code à ${SMS_CODE_LENGTH} chiffres reçu par SMS.`);
			analytics.trackValidationError(STEP_NUMBER, 'SMS_CODE_FORMAT', 'smsCode');
		}

		this.setState({ errors });
		return errors.length === 0;
	}

	verifyWithBackend = async () => {
		if (!this.validateSelection()) {
			return false;
		}

		if (!this.props.smsSent) {
			this.setState({
				errors: ['Veuillez attendre la réception du SMS ou réessayer l\'envoi.'],
			});
			return false;
		}

		const phoneNumber = (this.props.phoneNumber || '').replace(/\s/g, '');
		this.setState({ verifying: true, errors: [] });

		try {
			await verifySmsCode(phoneNumber, this.state.smsCode);
			this.setState({ verifying: false, phoneVerified: true });
			analytics.trackSmsVerified(STEP_NUMBER);
			return true;
		} catch (error) {
			const message = error.message || 'Le code saisi est incorrect ou a expiré.';
			this.setState({
				verifying: false,
				errors: [message],
			});
			analytics.trackValidationError(STEP_NUMBER, 'SMS_CODE_INVALID', 'smsCode');
			return false;
		}
	}

	isBusy = () => this.props.smsSending || this.state.verifying;

	render() {
		const { phoneNumber, smsSending, smsSent, smsSendError } = this.props;
		const { smsCode, errors, verifying } = this.state;
		const resendDisabled = this.isResendDisabled();

		return (
			<div className="question phone-confirmation">
				<h3>Confirmez votre numéro de téléphone</h3>
				{smsSending && <p>Un code de confirmation est en cours d'envoi...</p>}
				{smsSent && (
					<p>
						Un code à {SMS_CODE_LENGTH} chiffres a été envoyé par SMS au{' '}
						<strong>{this.maskPhoneNumber(phoneNumber)}</strong>.
					</p>
				)}
				{smsSendError && (
					<div className="validation-error" style={{ color: 'red', marginBottom: '10px' }}>
						<div>{smsSendError}</div>
						<button
							type="button"
							onClick={this.handleResendClick}
							disabled={resendDisabled}
							style={{ marginTop: '8px' }}
						>
							{this.getResendLabel('Réessayer l\'envoi')}
						</button>
					</div>
				)}
				<div className="contact-form sms-code-form">
					<div className="form-group sms-code-group">
						<input
							type="text"
							inputMode="numeric"
							pattern="[0-9]*"
							id="smsCode"
							name="smsCode"
							value={smsCode}
							onChange={this.handleChange}
							placeholder=""
							autoComplete="one-time-code"
							disabled={smsSending || verifying}
						/>
						<small>Saisissez le code reçu par SMS</small>
					</div>
				</div>
				{smsSent && (
					<button
						type="button"
						onClick={this.handleResendClick}
						disabled={resendDisabled}
						style={{ marginTop: '8px' }}
					>
						{this.getResendLabel('Renvoyer le code')}
					</button>
				)}
				{errors.length > 0 && (
					<div className="validation-error" style={{ color: 'red', marginTop: '10px' }}>
						{errors.map((error, idx) => (
							<div key={idx}>{error}</div>
						))}
					</div>
				)}
			</div>
		);
	}
}

export default PhoneConfirmation;
